import { useCallback, useRef } from "react";
import {
    ConsentStatusResource,
    PaymentInformationMessageResource,
    StatusResource,
    WebApiError,
} from "Shared/util/types";
import { getUserAgentResource } from "Shared/util/user-agent";
import { getOpenBankingConsentStatus } from "Shared/util/web-api";

type ConsentStatusPoller = {
    begin: (token: string) => void;
    disable: () => void;
    enable: () => void;
};

export const useConsentStatusPoller = (
    onSucceededStatus: () => void,
    onFailedStatus: (message: PaymentInformationMessageResource | null) => void,
    onFetchError: (message: PaymentInformationMessageResource | null) => void,
    onStatusUpdate?: (consentStatus: ConsentStatusResource) => void,
): ConsentStatusPoller => {
    const allowPolling = useRef(true);

    const disablePolling = useCallback(
        () => (allowPolling.current = false),
        [],
    );

    const enablePolling = useCallback(() => (allowPolling.current = true), []);

    const pollStatus = useCallback(
        (pollToken: string): void => {
            const userAgent = getUserAgentResource();

            getOpenBankingConsentStatus(pollToken, userAgent)
                .then((result: ConsentStatusResource) => {
                    if (onStatusUpdate) onStatusUpdate(result);
                    return result;
                })
                .then((result: ConsentStatusResource) => {
                    const { status, message } = result;

                    switch (status) {
                        case StatusResource.SUCCEEDED:
                            onSucceededStatus();
                            break;

                        case StatusResource.PENDING:
                            if (!allowPolling.current) break;
                            pollStatus(pollToken);
                            break;

                        case StatusResource.FAILED:
                            onFailedStatus(message);
                            break;

                        default:
                            throw new Error(
                                `Invalid ConsentStatusResource: ${status}`,
                            );
                    }
                })
                .catch(
                    ({
                        data,
                    }: WebApiError<PaymentInformationMessageResource | null>) => {
                        onFetchError(data ?? null);
                    },
                );
        },
        [onStatusUpdate, onSucceededStatus, onFailedStatus, onFetchError],
    );

    return {
        begin: pollStatus,
        disable: disablePolling,
        enable: enablePolling,
    };
};
